<template>
  <div class="UploadFile">
    <slot name="activator" v-bind:activatorProps="activatorProps">
      <v-btn
        color="primary"
        text
        @contextmenu.prevent="rightClick"
        @click="leftClick"
      >
        <v-icon v-if="!showText">{{ activatorCaption }}</v-icon>
        <div v-else>{{ activatorCaption }}</div>
      </v-btn>
    </slot>
    <v-dialog v-model="uploadDialog" width="80vw" max-height="80vh">
      <v-card>
        <v-container>
          <v-row>
            <v-col cols="6">
              <v-container>
                <v-row>
                  <v-col>
                    <h3 :title="titleTooltip">File attachments</h3>
                  </v-col>
                </v-row>
                <span>Manually upload an attachment through the web</span>
                <v-row>
                  <v-col>
                    <v-file-input id="tmcUploadFileImportFile"
                      class="tmc-uploadfile-importfile"
                      accept=".pdf,.jpg,.png,.tiff,.avif"
                      label="Import (.pdf,.jpg,.png,.tiff,.avif)"
                      multiple
                      small-chips
                      counter
                      show-size
                      clearable
                      :disabled="uploading"
                      @change="doUpload"
                      :key="afterUploadClearKey"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-data-table
                      :headers="headers"
                      :footer-props="footerProps"
                      :items="files"
                      :items-per-page="10"
                      dense
                      class="elevation-1"
                      @click:row="previewFile"
                    >
                      <template v-slot:[`item`]="{ item }">
                        <tr>
                          <td class="text-start clickable" @click="previewFile(item)">
                            {{ item.filename }}
                          </td>
                          <td class="text-start clickable" @click="previewFile(item)">
                            {{ item.lengthStr }}
                          </td>
                          <td class="text-start">
                            <v-icon small class="mr-2 tmc-uploadfile-eye-icon" tabindex="-1"
                            @click="openInNewTab(item)">
                              {{ icons.mdiEye }}
                            </v-icon>
                            <v-icon small class="mr-2 tmc-uploadfile-download-icon" tabindex="-1"
                            @click="download(item)">
                              {{ icons.mdiDownload }}
                            </v-icon>
                            <v-icon small class="mr-2 tmc-uploadfile-delete-icon" tabindex="-1"
                            @click="removeOne(item)">
                              {{ icons.mdiDelete }}
                            </v-icon>
                            <v-icon
                              v-if="mimeType(item).indexOf('image/') > -1"
                              small
                              class="mr-2"
                              tabindex="-1"
                              @click="rotateLft()"
                            >
                              {{ icons.mdiRotateLeft }}
                            </v-icon>
                            <v-icon
                              v-if="mimeType(item).indexOf('image/') > -1"
                              small
                              class="mr-2"
                              tabindex="-1"
                              @click="rotateRt()"
                            >
                              {{ icons.mdiRotateRight }}
                            </v-icon>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="6">
              <v-container>
                <v-row>
                  <v-col>
                    <PreviewFile
                      :src="previewSrc"
                      :file="activeFile"
                      :rotateImg="rotatedImg"
                      :isPreviewSupported="isPreviewSupported"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
          <v-row>
            <v-col v-if="uploading">
              <span>{{ statusText }}</span>
            </v-col>
            <v-spacer />
            <v-col id="tmcUploadFile"
            align="right">
              <v-btn
                class="tmc-upload-file-close-btn"
                icon
                @click="uploadDialog = false"
                >
                <v-icon>{{ icons.mdiClose }}</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <!-- confirm a delete; a file also attached to other records can be removed from the
      record(s) in view, or from all of them -->
    <v-dialog v-model="deleteDialog" persistent max-width="600">
      <v-card class="tmc-uploadfile-delete-dialog">
        <v-card-text class="pt-5 text-body-1">
          {{ deleteDialogMessage }}
        </v-card-text>
        <!-- flex-wrap: long labels ("Remove from these 12 records") wrap instead of scrolling -->
        <v-card-actions v-if="deleteStep === 'single'" class="flex-wrap justify-end">
          <v-btn class="tmc-uploadfile-cancel-btn" text :disabled="deleting"
            @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn class="tmc-uploadfile-confirm-delete-btn" color="error" :disabled="deleting"
            @click="removeHere">Delete</v-btn>
        </v-card-actions>
        <v-card-actions v-else-if="deleteStep === 'choose'" class="flex-wrap justify-end">
          <v-btn class="tmc-uploadfile-cancel-btn" text :disabled="deleting"
            @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn class="tmc-uploadfile-remove-all-btn" text color="error" :disabled="deleting"
            @click="askRemoveFromAll">Remove from all</v-btn>
          <v-btn class="tmc-uploadfile-remove-here-btn" text color="primary" :disabled="deleting"
            @click="removeHere">{{ removeHereText }}</v-btn>
        </v-card-actions>
        <v-card-actions v-else class="flex-wrap justify-end">
          <v-btn class="tmc-uploadfile-cancel-btn" text :disabled="deleting"
            @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn class="tmc-uploadfile-back-btn" text :disabled="deleting"
            @click="deleteStep = 'choose'">Back</v-btn>
          <v-btn class="tmc-uploadfile-confirm-remove-all-btn" color="error" :disabled="deleting"
            @click="removeFromAll">Yes, delete for all {{ deleteCounts.total }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-data-table v-if="showPending"
      class="v-data-table-pending-files elevation-4"
      dense
      :items="pendingFiles"
      :headers="pendingFilesHeaders"
      :footer-props="footerProps"
    >
      <template v-slot:footer>
      </template>
      <template v-slot:[`footer.page-text`]>
        <v-toolbar flat>
          <v-btn
            color="primary"
            class="mb-2 tmc-clear-pending-files-btn"
            @click="confirmClearPendingFiles"
          >
            Clear All
          </v-btn>
        </v-toolbar>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  mapGetters, mapActions, mapState, mapMutations,
} from 'vuex';
import {
  mdiClose,
  mdiDelete,
  mdiDownload,
  mdiEye,
  mdiRotateLeft,
  mdiRotateRight,
  mdiAttachment,
} from '@mdi/js';
import WhiteImageSmall from '@/assets/WhiteImageSmall.png';
// import FormData from 'form-data';
import { clients, isSessionExpired } from '../../util/clients';
import UploadFileMixin from './UploadFile.mixin';
import PreviewFile from './PreviewFile.vue';
import { mimeTypeByFileName, sanitizeFilename } from '../../util/shared/vue-global';
import {
  MAX_FILES_PER_UPLOAD,
  UPLOAD_CONCURRENCY,
  toFileArray,
  partitionByExtension,
  runWithConcurrency,
  summarizeResults,
  sharedFileCounts,
  isSharedElsewhere,
  alsoAttachedMessage,
  removeHereLabel,
  removeFromAllConfirmMessage,
} from './UploadFile.util';

const { backendRest } = clients.direct;

const uniqueTempIdForUser = (that) => {
  const timeStr = new Date().getTime();
  const userId = that.authUser;
  return `${userId}${timeStr}`;
};

const initdata = () => ({
  icons: {
    mdiClose,
    mdiDelete,
    mdiDownload,
    mdiEye,
    mdiRotateLeft,
    mdiRotateRight,
    mdiAttachment,
  },
  rotatedImg: '',
  rotationAngle: 0,
  headers: [
    { text: 'File name', value: 'filename' },
    { text: 'Size', value: 'lengthStr' },
    {
      text: 'Actions',
      value: 'actions',
      sortable: false,
    },
  ],
  pendingFilesHeaders: [
    { text: 'File name', value: 'filename' },
    { text: 'Size', value: 'lengthStr' },
  ],
  afterUploadClearKey: 0,
  uploadDialog: false,
  showPending: false,
  isActivelyUploading: false,
  timing: undefined,
  uploading: false,
  statusText: '',
  previewSrc: '',
  activeFile: undefined,
  previewSupport: ['image/png', 'image/jpeg', 'image/avif', 'application/pdf', 'image/tiff'],
  isPreviewSupported: true,
  deleteDialog: false,
  deleteStep: 'single', // 'single' | 'choose' | 'confirmAll'
  fileToDelete: null,
  deleting: false,
});

const calcRotation = (file) => {
  const rotate = file && file.metadata ? file.metadata.rotate || 0 : 0;
  return rotate % 4;
};

export default {
  name: 'UploadFile',
  components: {
    PreviewFile,
  },
  mixins: [UploadFileMixin],
  data: () => initdata(),
  watch: {
    uploadDialog(visible) {
      if (visible) {
        // Dialog was opened
        this.onOpened();

        /* worst case scenerio, if flag not got reset, reset it forcefully */
        this.isActivelyUploading = false;
      }
    },
  },

  props: {
    bucketName: {
      type: String,
      required: true,
    },
    program: {
      type: String,
      required: true,
    },
    parentObjectType: {
      type: String,
      required: true,
    },
    parentObjectId: {
      type: String,
      required: true,
    },
    itemIsSaved: {
      type: Boolean,
      required: true,
    },
    showText: {
      type: Boolean,
      default: true,
    },
    audit: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState({
      files: (state) => state.files.files,
      pendingFiles: (state) => state.files.pendingFiles,
    }),
    ...mapGetters('auth/token', ['authUser']),
    ...mapGetters('files', ['fileCount']),
    activatorCaption() {
      if (this.showText) {
        let uploadingStr = '';
        if (this.isActivelyUploading) {
          uploadingStr = '(UPLOADING...) ';
        }
        return `${uploadingStr}Upload File (${this.fileCount})`;
      }
      return this.icons.mdiAttachment;
    },
    titleTooltip() {
      return `(for: ${this.bucketName} - ${this.parentObjectType}:${this.parentObjectId})`;
    },
    activatorProps() {
      return {
        caption: this.activatorCaption,
        leftClick: this.leftClick,
        rightClick: this.rightClick,
      };
    },
    footerProps() {
      return {
        'show-current-page': true,
        'page-text': '',
        'disable-items-per-page': true,
      };
    },
    metadata() {
      const args = {
        bucketName: this.bucketName,
        program: this.program,
        parentObjectType: this.parentObjectType,
        parentObjectId: this.parentObjectId,
        audit: this.audit,
      };
      return args;
    },
    deleteCounts() {
      return sharedFileCounts(this.fileToDelete, this.parentObjectId)
        || { removing: 0, others: 0, total: 0 };
    },
    deleteDialogMessage() {
      if (this.deleteStep === 'single') return 'Are you sure you want to delete this file?';
      if (this.deleteStep === 'confirmAll') return removeFromAllConfirmMessage(this.deleteCounts.total);
      return alsoAttachedMessage(this.deleteCounts.others);
    },
    removeHereText() {
      return removeHereLabel(this.deleteCounts.removing);
    },
  },
  methods: {
    ...mapActions('files', [
      'backgroundAttachFiles',
      'loadAttachedFiles',
      'removeAttachedFile',
      'rotateImage',
      // 'loadPendingFiles',
      'clearPendingFiles',
    ]),
    ...mapMutations('files', ['setFiles']),
    ...mapActions(['flashInfo', 'flashWarn', 'flashError', 'reAuth']),
    // async hoverPendingFiles() {
    //   if (this.showText) {
    //     await this.loadPendingFiles();
    //     this.showPending = true;
    //   }
    // },
    setPendingFalse() {
      this.showPending = false;
    },
    // endHoverPendingFiles() {
    //   if (this.showText) {
    //     setTimeout(this.setPendingFalse, 3700);
    //   }
    // },
    confirmClearPendingFiles() {
      if (window.confirm('Are you sure?')) {
        this.clearPendingFiles();
        this.flashInfo('Cleared files pending / awaiting attachment!');
      }
    },
    mimeType(item) {
      const filename = item ? item.filename : '';
      return mimeTypeByFileName(filename);
    },
    async onOpened() {
      this.previewSrc = WhiteImageSmall;
      await this.loadAttachedFiles(this.metadata);
      if (this.files.length > 0) {
        this.previewFile(this.files[0]);
      }
    },
    async download(file) {
      const rotate = calcRotation(file);
      const url = await this.generateDowloadUrl(file, rotate);
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        window.open(url, '_blank');
      }
    },
    async previewFile(file) {
      const rotate = calcRotation(file);
      if (this.previewSupport.includes(this.mimeType(file))) {
        this.previewSrc = await this.generateDowloadUrl(file, rotate, 'preview');
        this.rotationAngle = 0;
        this.rotatedImg = `transform:rotate(${this.rotationAngle}deg)`;
        this.isPreviewSupported = true;
        this.activeFile = file;
      } else {
        this.previewSrc = WhiteImageSmall;
        this.activeFile = undefined;
        this.isPreviewSupported = false;
      }
    },
    async generateDowloadUrl(file, rotate, fetchType = 'download') {
      const { jwt } = await this.reAuth();
      const url = `${backendRest.defaults.baseURL}/doc/download/${this.program}/${file.id}?rotate=${rotate}&token=${jwt}&fetchType=${fetchType}`;
      return url;
    },
    async openInNewTab(file) {
      const rotate = calcRotation(file);
      const url = await this.generateDowloadUrl(file, rotate, 'preview');
      window.open(url, '_blank');
    },
    removeOne(file) {
      // a file also attached to other records (pick list multi-attach) asks which records first
      this.fileToDelete = file;
      this.deleteStep = isSharedElsewhere(file, this.parentObjectId) ? 'choose' : 'single';
      this.deleteDialog = true;
    },
    removeHere() {
      return this.deleteFile(this.fileToDelete, this.parentObjectId);
    },
    askRemoveFromAll() {
      this.deleteStep = 'confirmAll';
    },
    removeFromAll() {
      return this.deleteFile(this.fileToDelete);
    },
    // fileToDelete / deleteStep are left as they are so the text doesn't change while the dialog
    // fades out; removeOne sets both when it opens again.
    closeDeleteDialog() {
      this.deleteDialog = false;
    },
    // With parentObjectId the backend only detaches the file from those record(s) while it is
    // still attached to others; without it, it deletes the file for every record.
    async deleteFile(file, parentObjectId) {
      if (this.deleting) return;
      this.deleting = true;
      const { id, bucketName } = file;
      try {
        await this.removeAttachedFile({
          id,
          bucketName,
          audit: this.audit,
          ...(parentObjectId ? { parentObjectId } : {}),
        });
      } catch (error) {
        // the client already flashes "Not Authorized" for a 403
        if (error.message !== '403 Forbidden') {
          const reason = (error.message || '').replace(/^GraphQL error: /, '');
          this.flashError(`Unable to delete this file. ${reason}`.trim());
          this.onOpened();
          return;
        }
      } finally {
        this.deleting = false;
        this.closeDeleteDialog();
      }
      this.onOpened();
      this.fileDeletePaperClipUpdate();
    },
    rotateLft() {
      let angleDegree = this.rotationAngle - 90;
      if ([360, -360].includes(angleDegree)) {
        angleDegree = 0;
      }
      this.rotatedImg = `transform:rotate(${angleDegree}deg)`;
      this.rotationAngle = angleDegree;
    },
    rotateRt() {
      let angleDegree = this.rotationAngle + 90;
      if ([360, -360].includes(angleDegree)) {
        angleDegree = 0;
      }
      this.rotatedImg = `transform:rotate(${angleDegree}deg)`;
      this.rotationAngle = angleDegree;
    },
    rotate(file, howMuch) {
      const { id, metadata } = file;
      const sum = (metadata.rotate || 0) + howMuch;
      // don't let it inc/dec forever
      if (sum % 4 === 0) {
        metadata.rotate = 0;
      } else {
        metadata.rotate = sum;
      }
      this.rotateImage({
        id,
        rotate: metadata.rotate,
        bucketName: this.bucketName,
      });
      this.previewFile(file);
    },
    // v-file-input is `multiple`, so this gets a File[] (a bare File from older callers / tests).
    // Each file is its own POST, UPLOAD_CONCURRENCY at a time; one summary flash at the end.
    async doUpload(input) {
      const files = toFileArray(input);
      if (!files.length) {
        return;
      }
      if (files.length > MAX_FILES_PER_UPLOAD) {
        this.flashWarn(`You selected ${files.length} files. Please select ${MAX_FILES_PER_UPLOAD} or fewer at a time.`);
        this.afterUploadClearKey += 1;
        return;
      }
      const { accepted, rejected } = partitionByExtension(files);

      this.timing = new Date();
      this.uploading = true;
      this.statusText = `Uploading 0 / ${accepted.length}...`;

      // Stop sending after a 403 ('permission': the REST interceptor flashes "Not Authorized" for
      // each one) or once the session is gone ('session': the rest would fail the same way).
      let stopReason;
      let finished = 0;
      const results = await runWithConcurrency(accepted, UPLOAD_CONCURRENCY, async (file, index) => {
        const result = await this.uploadOne(file, index);
        if (result.sessionEnded) {
          stopReason = 'session';
        } else if (result.status === 403 && !stopReason) {
          stopReason = 'permission';
        }
        finished += 1;
        this.statusText = `Uploading ${finished} / ${accepted.length}...`;
        return result;
      }, () => !!stopReason);

      const anyUploaded = results.some((result) => result.ok);
      // Nothing uploaded because of a real 403: the interceptor's "Not Authorized" is the useful
      // message, so don't bury it under a summary.
      if (anyUploaded || stopReason !== 'permission') {
        const { level, message } = summarizeResults(
          results.map((result, i) => ({ name: accepted[i].name, ...result })),
          rejected,
          stopReason,
        );
        this[level](message);
      }

      const elapsed = new Date().getTime() - this.timing.getTime();
      this.statusText = `Done. Time elapsed: ${elapsed / 1000}`;
      this.afterUploadClearKey += 1;
      // The attached-files list is shared Vuex state. If this dialog was closed (or the component
      // destroyed) mid-batch, the user may now be viewing another record's attachments; reloading
      // this record's would replace them. Reopening the dialog reloads anyway.
      if (anyUploaded && this.uploadDialog && !this._isDestroyed) {
        await this.loadAttachedFiles(this.metadata);
      }
      this.uploading = false;
      if (anyUploaded) {
        this.fileUploadPaperClipUpdate(true);
      }
    },
    // Resolves { ok, name, status?, sessionEnded? } and never throws, so one bad file can't stop
    // a batch.
    async uploadOne(file, index) {
      // A batch can outlast the token: re-check it per file. If it has expired this opens the
      // re-login dialog, and every lane waits on that one login.
      try {
        await this.reAuth();
      } catch (error) {
        return { ok: false, name: file.name, sessionEnded: true };
      }
      const { program, parentObjectType, parentObjectId } = this;
      const metadataStr = JSON.stringify({
        rotate: 0,
        program,
        parentObjectType,
        parentObjectId,
      });

      const uniqueTempId = `${uniqueTempIdForUser(this)}-${index}`;
      const form = new FormData();
      form.append('id', uniqueTempId);
      form.append('file', sanitizeFilename(file.name));
      form.append('bucket', this.bucketName);
      form.append('metadata', metadataStr);
      form.append('data', file);

      const formHeaders = {
        'Content-Type': 'multipart/form-data',
      };
      try {
        const results = await backendRest.post(`/doc/upload/${uniqueTempId}`, form, {
          headers: formHeaders,
        });
        const ok = !!(results && results.data && results.data.status === 'success');
        return { ok, name: file.name };
      } catch (error) {
        const status = error && error.response ? error.response.status : undefined;
        // reAuth() passes a token for up to 20s after `exp`, and the backend answers an expired
        // token with 403, so tell that apart from a real permission problem.
        const sessionEnded = status === 403 && isSessionExpired();
        return {
          ok: false, name: file.name, status, sessionEnded,
        };
      }
    },
    async rightClick(unitTestParams) {
      let respose;
      if (this.itemIsSaved) {
        /* It will take care, if some process is already going in background */
        if (!this.isActivelyUploading) {
          this.isActivelyUploading = true;
          this.flashInfo('Background attaching scanned images...');
          respose = await this.backgroundAttachFiles({ args: this.metadata, unitTestParams });
          await this.loadAttachedFiles(this.metadata);
          this.fileUploadPaperClipUpdate();
          this.isActivelyUploading = false;
        }
      } else {
        // a somewhat sane default behavior, for the default activator button
        this.flashWarn('You cannot attach images until your record is saved.');
        this.$emit('pre:save');
      }
      return respose;
    },
    leftClick() {
      this.loadAttachedFiles(this.metadata);
      if (this.itemIsSaved) {
        this.uploadDialog = true;
      } else {
        // a somewhat sane default behavior, for the default activator button
        this.flashWarn('You cannot attach images until your record is saved.');
        this.$emit('pre:save');
      }
    },
  },
};
</script>

<style lang="sass">
td.clickable:hover
  cursor: pointer
  font-weight: 600
.v-data-table-pending-files
  position: absolute
  bottom: 10vh
  right: 5vw
.tmc-uploadfile-delete-dialog .v-card__actions
  row-gap: 4px
</style>
